import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Copy, Code2, ChevronDown, Check } from 'lucide-react';

const LANGUAGES = [
  { id: 'curl',               label: 'cURL',              icon: '🔌' },
  { id: 'javascript_fetch',   label: 'JS Fetch',          icon: '🌐' },
  { id: 'javascript_axios',   label: 'JS Axios',          icon: '⚡' },
  { id: 'nodejs_axios',       label: 'Node.js Axios',     icon: '🟩' },
  { id: 'python_requests',    label: 'Python requests',   icon: '🐍' },
  { id: 'python_httpx',       label: 'Python httpx',      icon: '🐍' },
  { id: 'go',                 label: 'Go',                icon: '🐹' },
  { id: 'java',               label: 'Java',              icon: '☕' },
  { id: 'php',                label: 'PHP',               icon: '🐘' },
  { id: 'ruby',               label: 'Ruby',              icon: '💎' },
];

export default function CodeSnippetPanel({ endpointId, environments }) {
  const [lang, setLang] = useState('curl');
  const [envId, setEnvId] = useState('');
  const [snippet, setSnippet] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (environments?.length && !envId) setEnvId(environments[0]?.id || '');
  }, [environments]);

  useEffect(() => {
    if (endpointId) generate();
  }, [endpointId, lang, envId]);

  async function generate() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ language: lang });
      if (envId) params.append('environment_id', envId);
      const res = await api.get(`/endpoints/${endpointId}/snippet?${params}`);
      setSnippet(res.data.snippet);
    } catch { setSnippet('// Failed to generate snippet'); }
    finally { setLoading(false); }
  }

  function copy() {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  }

  if (!endpointId) return (
    <div style={sty.empty}>
      <Code2 size={36} style={{ opacity: 0.15 }} />
      <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Select an endpoint to generate code</p>
    </div>
  );

  const activeLang = LANGUAGES.find(l => l.id === lang);

  return (
    <div style={sty.root}>
      {/* Toolbar */}
      <div style={sty.toolbar}>
        <div style={sty.langPicker}>
          <span style={sty.activeLangIcon}>{activeLang?.icon}</span>
          <select style={sty.langSelect} value={lang} onChange={e => setLang(e.target.value)}>
            {LANGUAGES.map(l => (
              <option key={l.id} value={l.id}>{l.icon} {l.label}</option>
            ))}
          </select>
          <ChevronDown size={12} style={{ color: 'var(--text-muted)', pointerEvents: 'none' }} />
        </div>

        <select style={sty.envSelect} value={envId} onChange={e => setEnvId(e.target.value)}>
          <option value="">No Environment</option>
          {(environments || []).map(e => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>

        <button style={sty.copyBtn} onClick={copy} disabled={!snippet}>
          {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy</>}
        </button>
      </div>

      {/* Snippet display */}
      <div style={sty.codeWrapper}>
        <div style={sty.codeHeader}>
          <span style={sty.codeLabel}>{activeLang?.label}</span>
          {loading && <span style={sty.loadingDot}>generating...</span>}
        </div>
        <pre style={sty.code} className="mono">
          {loading ? '// Generating...' : snippet || '// No snippet generated'}
        </pre>
      </div>

      {/* Language grid quick-select */}
      <div style={sty.langGrid}>
        {LANGUAGES.map(l => (
          <button
            key={l.id}
            style={{ ...sty.langChip, ...(lang === l.id ? sty.langChipActive : {}) }}
            onClick={() => setLang(l.id)}
          >
            {l.icon} {l.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const sty = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: 'var(--bg-primary)' },
  empty: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', flexShrink: 0 },
  langPicker: { display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 7, padding: '5px 10px', position: 'relative' },
  activeLangIcon: { fontSize: 14 },
  langSelect: { background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)', paddingRight: 18, appearance: 'none', fontWeight: 500 },
  envSelect: { background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)', borderRadius: 7, color: 'var(--text-secondary)', fontSize: 12, padding: '5px 10px', cursor: 'pointer', minWidth: 130 },
  copyBtn: { display: 'flex', alignItems: 'center', gap: 5, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--sans)', marginLeft: 'auto', transition: 'all 0.15s' },
  codeWrapper: { flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' },
  codeHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' },
  codeLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  loadingDot: { fontSize: 11, color: 'var(--accent)', animation: 'pulse 1s infinite' },
  code: { flex: 1, padding: '20px 24px', fontSize: 12, color: '#a8d8a8', lineHeight: 1.75, whiteSpace: 'pre', overflow: 'auto', margin: 0, fontFamily: 'var(--mono)' },
  langGrid: { display: 'flex', flexWrap: 'wrap', gap: 6, padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)', flexShrink: 0 },
  langChip: { display: 'flex', alignItems: 'center', gap: 5, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--sans)', color: 'var(--text-secondary)', transition: 'all 0.12s' },
  langChipActive: { background: 'var(--accent-dim)', borderColor: 'var(--accent)', color: 'var(--accent)' },
};
